import { type ReactNode } from 'react';
import { Portal } from '@headlessui/react';
import clsx from 'clsx';
import { useToaster, type Toast } from 'react-hot-toast/headless';
import { useMedia } from 'react-use';
import { useModalTransition } from '@hungryroot/ui-kit';

import styles from './ToastContainer.module.css';

const DEFAULT_TOAST_DURATION = 5000;

type ToastPositionVariant = 'bottom-center' | 'top-center' | 'relative';

const getPositionClassName = (position: ToastPositionVariant) => {
  switch (position) {
    case 'bottom-center':
      return styles['bottom-center'];
    case 'top-center':
      return styles['top-center'];
    default:
      return '';
  }
};

interface Props {
  testid?: string;
  position?: ToastPositionVariant;
  duration?: number;
  containerClassName?: string;
}

interface RenderedToastProps {
  toast: Toast;
}

function RenderedToast(props: RenderedToastProps) {
  const { toast } = props;
  const { visible, ariaProps, message: toastMessage } = toast;
  // When the user prefers reduced motion the CSS `transition: none` rule
  // means `transitionend` never fires — opt out of the leave animation
  // entirely so the toast unmounts immediately instead of waiting for the
  // 400ms safety timeout.
  const prefersReducedMotion = useMedia(
    '(prefers-reduced-motion: reduce)',
    false
  );

  const { shouldRender, closing, handleTransitionEnd } = useModalTransition({
    isOpen: visible,
    skipTransition: prefersReducedMotion ? true : false,
  });

  if (!shouldRender) {
    return null;
  }

  return (
    <div
      className={clsx(styles.toast, {
        [styles['toast-leaving']]: closing,
      })}
      {...ariaProps}
      onTransitionEnd={handleTransitionEnd}
    >
      {toastMessage as ReactNode}
    </div>
  );
}

export function ToastContainer(props: Props) {
  const {
    testid,
    duration,
    position = 'bottom-center',
    containerClassName,
  } = props;
  const { toasts } = useToaster({
    duration: typeof duration === 'number' ? duration : DEFAULT_TOAST_DURATION,
  });

  const className =
    position === 'bottom-center' || position === 'top-center'
      ? styles.fixed
      : styles.none;

  const body = (
    <div
      data-testid={testid}
      // Marks the toast region so modal overlays can exclude it from their
      // interact-outside dismiss (tapping a toast must not close a modal).
      data-toast-region=""
      // react-aria's `ariaHideOutside` marks everything outside an open modal
      // as `inert`, which would make toasts non-interactive over RDP/PDP
      // modals. This opt-out keeps the toast (and its buttons) clickable.
      data-react-aria-top-layer="true"
      className={clsx(
        {
          [styles['has-toasts-to-render']]: toasts.length > 0,
        },
        className,
        getPositionClassName(position),
        containerClassName
      )}
    >
      {toasts.map((toast) => {
        return <RenderedToast key={toast.id} toast={toast} />;
      })}
    </div>
  );

  if (position !== 'relative') {
    return <Portal data-testid="toasts-container">{body}</Portal>;
  }

  return body;
}
